import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Form, Formik } from 'formik';
import styled from 'styled-components';
import * as Yup from 'yup';
import {
  Avatar,
  Box,
  Button,
  Card,
  Grid,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core';
import {
  Add as AddIcon,
  Cancel as CancelIcon,
  People as PeopleIcon
} from '@material-ui/icons';
import { Alert, Autocomplete } from '@material-ui/lab';

import CardContent from '../common/card/CardContent';
import CardTitle from '../common/card/CardTitle';
import WrappedTableCell from '../common/WrappedTableCell';
import { useUpdateSignup } from '../login/Signup.hooks';
import { BaseOnboardingProps, SignupUser } from '../login/Signup.interface';
import { useNotifySupport } from '../user/User.hooks';

export const functionalRoles = [
  'Quality Assurance',
  'Compliance',
  'Regulatory Affairs',
  'Product Management',
  'Engineering',
  'Human Resources',
  'Clinical Affairs',
  'Subject Matter Expert (Content Reviewer, Approver)',
  'Training Administrator',
  'Test Engineer',
  'Document Manager',
  'CAPA Manager',
  'Other'
];

const StyledCard = styled(Card)`
  position: relative;
  &:not(:last-child) {
    margin-bottom: 24px;
  }
  .card-content {
    padding: 32px;
    .description {
      margin-top: 12px;
      margin-bottom: 24px;
    }
  }
  .actions-grid {
    display: flex;
    align-items: center;
    .MuiButtonBase-root:not(:last-child) {
      margin-right: 8px;
    }
  }
  .dense {
    padding-top: 2px;
    padding-bottom: 2px;
  }
  .avatar-cell {
    padding-top: 2px;
    padding-bottom: 2px;
    padding-right: 4px;
    .avatar {
      width: 32px;
      height: 32px;
      font-size: 16px;
    }
  }
`;

const OnboardingInviteUsers: React.FC<BaseOnboardingProps> = props => {
  const { signup, onContinue, onChange, onUpdate, onError } = props;
  const onChangeRef = useRef(onChange);
  const onUpdateRef = useRef(onUpdate);
  const onErrorRef = useRef(onError);
  const {
    signup: updatedSignup,
    isLoading: isLoadingSignup,
    error: signupError,
    update
  } = useUpdateSignup(signup.id);
  const {
    data: isSupportNotified,
    isLoading: notifyingSupport,
    error: notifyError,
    notifySupport
  } = useNotifySupport(signup.userId);
  const isLoading = isLoadingSignup || notifyingSupport;
  const error = signupError || notifyError;
  const [users, setUsersState] = useState(() => signup.users || []);

  const validationSchema = Yup.object({
    firstName: Yup.string().required('Required'),
    lastName: Yup.string().required('Required'),
    role: Yup.string().required('Required'),
    email: Yup.string()
      .lowercase()
      .email('Must be a valid email address.')
      .required('Required')
      .test('duplicate-user-email', 'Email already exists in list.', value => {
        return !users.some(user => user.email === value);
      })
  });

  // set and update users
  const setUsers: typeof setUsersState = useCallback(
    value => {
      setUsersState(users => {
        const updated = typeof value === 'function' ? value(users) : value;
        update({ users: updated });
        return updated;
      });
    },
    [update]
  );

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);
  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    setUsersState(signup.users);
  }, [signup.users]);

  useEffect(() => {
    if (updatedSignup) {
      onChangeRef.current?.(updatedSignup);
      onUpdateRef.current?.();
    }
  }, [updatedSignup]);

  useEffect(() => {
    if (error) {
      onErrorRef.current?.(error);
    }
  }, [error]);

  useEffect(() => {
    // proceed to next step when support is notified
    if (isSupportNotified) {
      onContinue?.();
    }
  }, [isSupportNotified]);

  const submitButton = (
    <Box mb={4} mx={4}>
      <Button
        size='large'
        color='primary'
        variant='contained'
        disabled={isLoading}
        onClick={() => notifySupport()}
      >
        Submit
      </Button>
    </Box>
  );

  return (
    <>
      <StyledCard>
        <CardContent className='card-content'>
          <Typography variant='h2'>Invite Employees to Join</Typography>
          <Typography variant='subtitle1' className='description'>
            Add and manage permissions of your team members.
          </Typography>
          <Formik<SignupUser>
            validationSchema={validationSchema}
            initialValues={{ firstName: '', lastName: '', email: '', role: '' }}
            onSubmit={(item, formik) => {
              setUsers(users => users.concat(item));
              formik.resetForm();
            }}
          >
            {({
              values,
              dirty,
              errors,
              touched,
              isValid,
              handleChange,
              handleBlur,
              resetForm,
              setFieldValue
            }) => (
              <Form>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      size='small'
                      name='firstName'
                      label='First Name'
                      variant='outlined'
                      placeholder='Enter first name'
                      value={values.firstName}
                      error={touched.firstName && !!errors.firstName}
                      helperText={touched.firstName && errors.firstName}
                      onBlur={handleBlur}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      size='small'
                      name='lastName'
                      label='Last Name'
                      variant='outlined'
                      placeholder='Enter last name'
                      value={values.lastName}
                      error={touched.lastName && !!errors.lastName}
                      helperText={touched.lastName && errors.lastName}
                      onBlur={handleBlur}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={4}>
                    <TextField
                      fullWidth
                      size='small'
                      name='email'
                      label='Email'
                      variant='outlined'
                      placeholder='Enter email address'
                      value={values.email}
                      error={touched.email && !!errors.email}
                      helperText={touched.email && errors.email}
                      onBlur={handleBlur}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={12}>
                    <Autocomplete
                      value={values.role || ''}
                      options={functionalRoles}
                      onBlur={handleBlur}
                      onChange={(_, value) => {
                        setFieldValue('role', value || '', true);
                      }}
                      renderInput={params => (
                        <TextField
                          {...params}
                          fullWidth
                          size='small'
                          name='role'
                          variant='outlined'
                          label='Functional Role'
                          error={touched.role && !!errors.role}
                          helperText={touched.role && errors.role}
                          placeholder='Select functional role'
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} className='actions-grid'>
                    <div style={{ flex: 1 }} />
                    <Button disabled={!dirty} onClick={() => resetForm()}>
                      Clear
                    </Button>
                    <Button
                      type='submit'
                      color='primary'
                      variant='outlined'
                      disabled={!isValid}
                      endIcon={<AddIcon />}
                    >
                      Add Employee
                    </Button>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </CardContent>
        {users.length === 0 && submitButton}
      </StyledCard>

      {users.length > 0 && (
        <StyledCard>
          {isLoading && <LinearProgress className='progress' />}
          <CardTitle>
            <PeopleIcon />
            Users in the organization ({users.length})
          </CardTitle>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell style={{ width: 0 }} />
                  <TableCell>Employee</TableCell>
                  <TableCell style={{ width: '40%' }}>
                    Functional Role
                  </TableCell>
                  <TableCell style={{ width: 0 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user, index) => (
                  <TableRow key={user.email}>
                    <TableCell className='avatar-cell'>
                      <Avatar className='avatar'>
                        {user.firstName
                          .slice(0, 1)
                          .concat(user.lastName.slice(0, 1))
                          .toUpperCase()}
                      </Avatar>
                    </TableCell>
                    <WrappedTableCell>
                      {user.firstName} {user.lastName} ({user.email})
                    </WrappedTableCell>
                    <TableCell className='dense'>
                      <Autocomplete
                        disableClearable
                        value={user.role}
                        options={functionalRoles}
                        onChange={(_, value) => {
                          if (!value) {
                            return;
                          }
                          setUsers(users => {
                            const user = { ...users[index], role: value };
                            const copy = Array.from(users);
                            copy.splice(index, 1, user);
                            return copy;
                          });
                        }}
                        renderInput={params => (
                          <TextField
                            {...params}
                            fullWidth
                            name='role'
                            size='small'
                            variant='outlined'
                            placeholder='Select functional role'
                          />
                        )}
                      />
                    </TableCell>
                    <TableCell className='dense'>
                      <IconButton
                        size='small'
                        onClick={() => {
                          setUsers(users => {
                            const copy = Array.from(users);
                            copy.splice(index, 1);
                            return copy;
                          });
                        }}
                      >
                        <CancelIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box mt={2} mb={3} mx={4}>
            <Alert severity='info'>
              Users listed above will be notified once your trial environment is
              set up. You can invite more Users through QMS later on.
            </Alert>
          </Box>
          {submitButton}
        </StyledCard>
      )}
    </>
  );
};

export default OnboardingInviteUsers;
